import React, { useState, useEffect, useCallback, useMemo } from 'react';
import moment from 'moment';
import Cookies from 'js-cookie';

import 'moment/locale/ja';

moment.locale('ja');

const SHIFTS_API = 'https://system.sales-management.cloud/api/shifts.php';
const WEEKDAYS = ['日', '月', '火', '水', '木', '金', '土'];

const STATUS_STYLES = {
  on_time: { label: '定刻', bgColor: 'bg-green-100', borderColor: 'border-green-300', textColor: 'text-green-700' },
  late_early: { label: '遅早', bgColor: 'bg-rose-100', borderColor: 'border-rose-300', textColor: 'text-rose-700' },
  late: { label: '遅刻', bgColor: 'bg-amber-100', borderColor: 'border-amber-300', textColor: 'text-amber-700' },
  early_leave: { label: '早退', bgColor: 'bg-orange-100', borderColor: 'border-orange-300', textColor: 'text-orange-700' },
  off: { label: '休み', bgColor: 'bg-gray-100', borderColor: 'border-gray-300', textColor: 'text-gray-600' },
  absent: { label: '欠勤', bgColor: 'bg-red-200', borderColor: 'border-red-400', textColor: 'text-red-800' },
  absent_today: { label: '当欠', bgColor: 'bg-red-100', borderColor: 'border-red-300', textColor: 'text-red-700' }
};

const toMinutes = (timeValue) => {
  if (!timeValue) return null;
  const base = String(timeValue).substring(0, 5);
  if (!/^\d{2}:\d{2}$/.test(base)) return null;
  const [hour, minute] = base.split(':').map(Number);
  return hour * 60 + minute;
};

function Shift() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState({});
  const [attendance, setAttendance] = useState({});

  // 認証データ取得
  const getAuthData = useCallback(() => {
    const token = Cookies.get('auth_token');
    const userInfo = Cookies.get('user_info');
    const user = userInfo ? JSON.parse(userInfo) : null;
    return { token, user };
  }, []);

  // シフトデータ取得
  const fetchShifts = useCallback(async (date = currentDate) => {
    setLoading(true);
    setError('');
    
    try {
      const { token } = getAuthData();
      
      if (!token) {
        setError('認証情報が見つかりません');
        return;
      }


      const year = moment(date).format('YYYY');
      const month = moment(date).format('M');

      const response = await fetch(`${SHIFTS_API}?year=${year}&month=${month}`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      

      if (data.status === 'success') {
        setUsers(data.users || []);
        
        const shiftsMap = {};
        const attendanceMap = {};
        if (data.data) {
          Object.keys(data.data).forEach(dateKey => {
            const dayData = data.data[dateKey];
            shiftsMap[dateKey] = dayData.shifts || [];
            attendanceMap[dateKey] = dayData.attendance || [];
          });
        }
        
        setShifts(shiftsMap);
        setAttendance(attendanceMap);
      } else {
        setError(data.message || 'シフトの取得に失敗しました');
      }
    } catch (err) {
      console.error('Fetch error:', err);
      setError('通信エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }, [currentDate, getAuthData]);

  useEffect(() => {
    fetchShifts();
  }, [fetchShifts]);

  // 週移動
  const navigateWeek = useCallback((offset) => {
    const newDate = moment(currentDate).add(offset, 'week').toDate();
    setCurrentDate(newDate);
  }, [currentDate]);

  const handlePrevMonth = () => navigateWeek(-1);
  const handleNextMonth = () => navigateWeek(1);
  const handleToday = useCallback(() => {
    setCurrentDate(new Date());
  }, []);

  // 今が属する週から5週分の日付配列を生成
  const getWeeksFromToday = useCallback(() => {
    const today = moment(currentDate);
    const weekStart = today.clone().startOf('week'); // 日曜日から始まる
    
    const days = [];
    for (let i = 0; i < 35; i++) { // 5週間 × 7日
      days.push(weekStart.clone().add(i, 'day'));
    }
    
    return days;
  }, [currentDate]);

  // ユーザーのシフト取得
  const getUserShiftForDate = useCallback((userId, date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const dayShifts = shifts[dateStr] || [];
    return dayShifts.find(shift => shift.user_id === userId);
  }, [shifts]);

  const getUserAttendanceForDate = useCallback((userId, date) => {
    const dateStr = moment(date).format('YYYY-MM-DD');
    const dayAttendance = attendance[dateStr] || [];
    return dayAttendance.find((att) => Number(att.user_id) === Number(userId)) || null;
  }, [attendance]);

  const getAttendanceState = useCallback((day, shift, att) => {
    const isToday = day.isSame(moment(), 'day');
    const isPast = day.isBefore(moment(), 'day');
    const shiftIn = shift ? (shift.start_time || shift.shift_in || '') : '';
    const shiftOut = shift ? (shift.end_time || shift.shift_out || '') : '';
    const hasSchedule = !!shift;

    const clockIn = att && att.clock_in ? String(att.clock_in).substring(0, 5) : '';
    const clockOut = att && att.clock_out ? String(att.clock_out).substring(0, 5) : '';

    if (!hasSchedule) {
      return 'off';
    }

    // 過去日でシフトありだが出退勤記録なし → 欠勤
    if (isPast && !clockIn && !clockOut) {
      return 'absent';
    }

    if (isToday && !clockIn && !clockOut) {
      // シフト開始時刻を過ぎている場合のみ当日欠勤とみなす
      const nowMinutes = moment().hours() * 60 + moment().minutes();
      const shiftInMins = toMinutes(shiftIn);
      if (shiftInMins === null || nowMinutes >= shiftInMins) {
        return 'absent_today';
      }
      // シフト開始前はまだ出勤前として通常扱い（on_time へフォールスルー）
    }

    const shiftInMinutes = toMinutes(shiftIn);
    const shiftOutMinutes = toMinutes(shiftOut);
    const clockInMinutes = toMinutes(clockIn);
    const clockOutMinutes = toMinutes(clockOut);

    const isLate = clockInMinutes !== null && shiftInMinutes !== null && clockInMinutes > shiftInMinutes;
    const isEarlyLeave = clockOutMinutes !== null && shiftOutMinutes !== null && clockOutMinutes < shiftOutMinutes;

    if (isLate && isEarlyLeave) {
      return 'late_early';
    }

    if (isLate) {
      return 'late';
    }
    if (isEarlyLeave) {
      return 'early_leave';
    }

    return 'on_time';
  }, []);

  const weekDays = getWeeksFromToday();
  const weeks = [];
  for (let i = 0; i < weekDays.length; i += 7) {
    weeks.push(weekDays.slice(i, i + 7));
  }

  const visibleUsers = useMemo(() => {
    const activeUserIds = new Set();
    weekDays.forEach((day) => {
      const dateKey = day.format('YYYY-MM-DD');
      const dayShifts = shifts[dateKey] || [];
      (dayShifts || []).forEach((shift) => {
        const hasShiftTime = !!(
          shift &&
          ((shift.shift_in && String(shift.shift_in).trim() !== '') ||
            (shift.shift_out && String(shift.shift_out).trim() !== ''))
        );
        if (hasShiftTime && shift.user_id != null) {
          activeUserIds.add(Number(shift.user_id));
        }
      });
    });
    return users.filter((user) => activeUserIds.has(Number(user.user_id)));
  }, [users, shifts, weekDays]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-3 md:p-4 lg:p-6">
      <div className="max-w-full mx-auto">
        {/* ヘッダー */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 md:gap-4">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800">シフト表</h1>
            
            <div className="flex items-center gap-2 md:gap-3">
              <button
                onClick={handlePrevMonth}
                className="px-3 md:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors text-sm md:text-base"
              >
                ← 前週
              </button>
              
              <button
                onClick={handleToday}
                className="px-3 md:px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white font-medium rounded-md transition-colors text-sm md:text-base"
              >
                今週
              </button>
              
              <button
                onClick={handleNextMonth}
                className="px-3 md:px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-md transition-colors text-sm md:text-base"
              >
                次週 →
              </button>
              
              <span className="text-lg md:text-xl font-semibold text-gray-700 ml-2 md:ml-4">
                {moment(currentDate).startOf('week').format('YYYY年M月D日（ddd）')} より5週間
              </span>
            </div>
          </div>
        </div>

        {/* アラート */}
        {error && (
          <div className="mb-4 p-3 md:p-4 bg-red-50 border border-red-200 rounded-lg text-sm md:text-base text-red-700">
            {error}
          </div>
        )}

        {/* ローディング */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mr-3"></div>
            読み込み中...
          </div>
        ) : (
          <>
            {/* 凡例 */}
            <div className="bg-white rounded-lg shadow-sm p-4 md:p-5 mb-4 md:mb-6">
              <h3 className="text-sm md:text-base font-semibold text-gray-700 mb-3">凡例</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {Object.entries(STATUS_STYLES).map(([key, config]) => (
                  <div key={key} className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded border ${config.borderColor} ${config.bgColor}`}></div>
                    <span className="text-xs md:text-sm text-gray-700">{config.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* シフト表 */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              {weeks.map((week, weekIndex) => (
                <div key={weekIndex} className="border-b border-gray-200">
                  {/* 日付ヘッダー */}
                  <div className="grid grid-cols-8 gap-0 bg-gray-50 border-b border-gray-200">
                    {/* ユーザー名用ヘッダーセル */}
                    <div className="p-2 md:p-3 border-r border-gray-200 bg-gradient-to-r from-slate-100 to-transparent"></div>
                    {week.map((day) => {
                      const isCurrentMonth = day.format('YYYY-MM') === moment(currentDate).format('YYYY-MM');
                      const isToday = day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
                      
                      return (
                        <div
                          key={day.format('YYYY-MM-DD')}
                          className={`p-2 md:p-3 text-center border-r border-gray-200 last:border-r-0 ${
                            isCurrentMonth ? 'bg-white' : 'bg-gray-100'
                          } ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                        >
                          <p className={`text-xs md:text-sm font-semibold ${
                            isToday ? 'text-blue-600' :
                            day.day() === 0 ? 'text-red-600' :
                            day.day() === 6 ? 'text-blue-600' :
                            'text-gray-700'
                          }`}>
                            {day.format('D')}
                          </p>
                          <p className={`text-[10px] md:text-xs ${
                            day.day() === 0 ? 'text-red-500' :
                            day.day() === 6 ? 'text-blue-500' :
                            'text-gray-500'
                          }`}>
                            {WEEKDAYS[day.day()]}
                          </p>
                          {isToday && <p className="text-[9px] text-blue-600 font-semibold mt-0.5">今日</p>}
                        </div>
                      );
                    })}
                  </div>

                  {/* ユーザー別シフト行 */}
                  <div>
                    {visibleUsers.map((user, userIndex) => (
                      <div
                        key={user.user_id}
                        className={`grid grid-cols-8 gap-0 border-b border-gray-200 last:border-b-0 ${
                          userIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        {/* ユーザー名列 */}
                        <div className="col-span-1 p-2 md:p-3 border-r border-gray-200 bg-gradient-to-r from-slate-50 to-transparent flex items-center justify-center min-h-12 md:min-h-14">
                          <div className="text-center">
                            <p className="text-[11px] md:text-xs font-semibold text-gray-800 truncate px-1">
                              {user.name || `User ${user.user_id}`}
                            </p>
                            <p className="text-[9px] text-gray-500">{user.emoji || ''}</p>
                          </div>
                        </div>

                        {/* シフト列 */}
                        {week.map((day) => {
                          const isCurrentMonth = day.format('YYYY-MM') === moment(currentDate).format('YYYY-MM');
                          const isToday = day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');
                          const isPast = day.isBefore(moment(), 'day');
                          const isPastOrToday = isPast || isToday;
                          const shift = getUserShiftForDate(user.user_id, day);
                          const att = getUserAttendanceForDate(user.user_id, day);
                          const stateKey = getAttendanceState(day, shift, att);
                          const stateConfig = STATUS_STYLES[stateKey] || STATUS_STYLES.on_time;
                          const startTimeLabel = shift ? (shift.start_time || shift.shift_in || '') : '';
                          const clockInLabel = att && att.clock_in ? String(att.clock_in).substring(0, 5) : '--:--';
                          const clockOutLabel = att && att.clock_out ? String(att.clock_out).substring(0, 5) : '--:--';

                          return (
                            <div
                              key={`${user.user_id}-${day.format('YYYY-MM-DD')}`}
                              className={`p-2 md:p-3 border-r border-gray-200 last:border-r-0 flex items-center justify-center min-h-12 md:min-h-14 ${
                                isCurrentMonth ? 'bg-white' : 'bg-gray-100'
                              } ${isToday ? 'ring-2 ring-blue-500 ring-inset' : ''}`}
                            >
                              <div className={`flex flex-col items-center justify-center px-2 py-1 rounded transition-all ${stateConfig.bgColor} border ${stateConfig.borderColor} w-full text-center`}>
                                <span className={`text-xs md:text-sm font-bold ${stateConfig.textColor}`}>
                                  {stateConfig.label}
                                </span>
                                {startTimeLabel && (
                                  <span className="text-[8px] md:text-[10px] text-gray-600 mt-0.5">
                                    {startTimeLabel.substring(0, 5)}
                                  </span>
                                )}
                                {isPastOrToday && (
                                  <div className="text-[8px] md:text-[10px] text-gray-500 mt-0.5 leading-tight">
                                    <div>実</div>
                                    <div>出 {clockInLabel}</div>
                                    <div>退 {clockOutLabel}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {visibleUsers.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                表示するシフト情報がありません
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Shift;